import React, { useMemo } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import { Box, Chip } from '@mui/material'
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid'
import { StatusChip } from './StatusChip'

// Enhanced numeric parsing function - matches AutorizarCPPreview logic
const parseNumericValue = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Enhanced cleaning - remove currency symbols but preserve number format
    const cleanValue = value.replace(/[$\s]/g, '');
    
    // Handle formats like "5,460,922.00" properly
    if (cleanValue.includes(',') && cleanValue.includes('.')) {
      const lastComma = cleanValue.lastIndexOf(',');
      const lastDot = cleanValue.lastIndexOf('.');
      if (lastDot > lastComma) {
        // US format: 5,460,922.00 -> remove commas, keep decimal dot
        return parseFloat(cleanValue.replace(/,/g, '')) || 0;
      } else {
        // Colombian format: 5.460.922,00 -> remove dots, replace comma with dot
        return parseFloat(cleanValue.replace(/\./g, '').replace(',', '.')) || 0;
      }
    } else if (cleanValue.includes(',')) {
      // Only comma - determine if thousands or decimal
      const parts = cleanValue.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        // Decimal separator: 1234,50
        return parseFloat(cleanValue.replace(',', '.')) || 0;
      } else {
        // Thousands separator: 1,234,567
        return parseFloat(cleanValue.replace(/,/g, '')) || 0;
      }
    } else {
      // Simple number parsing
      return parseFloat(cleanValue) || 0;
    }
  }
  return 0;
};

// Utility functions for data formatting - reusable across the application
const formatCurrency = (value: any): string => {
  if (value == null || value === '' || value === 'nan') return ''
  
  // Use enhanced parsing function
  const numericValue = parseNumericValue(value);
  
  // Return formatted currency if we have a valid number
  if (!isNaN(numericValue) && numericValue !== 0) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericValue)
  }
  
  // Return empty string for invalid values to prevent [object Object]
  if (value === 'nan' || value === null || value === undefined) {
    return ''
  }
  
  // Return original value only if it's a simple string
  return value?.toString() || ''
}

const formatDate = (value: any): string => {
  if (!value || value === 'nan' || value === null || value === undefined) return ''
  
  // Handle "nan" strings and other invalid values explicitly
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'nan' || lowerValue === 'null' || lowerValue === 'undefined' || lowerValue === '') {
      return ''
    }
    
    // If already a formatted date string, return as is
    if (value.includes('/') || value.includes('-')) {
      // Handle DD/MM/YYYY format - convert to local display
      if (value.includes('/')) {
        const parts = value.split('/')
        if (parts.length === 3) {
          const [day, month, year] = parts
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('es-CO')
          }
        }
        // Return empty for invalid date formats to prevent [object Object]
        return ''
      }
      
      // Handle YYYY-MM-DD format or other formats
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-CO')
      }
      // Return empty for invalid dates to prevent [object Object]
      return ''
    }
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    if (!isNaN(value.getTime())) {
      return value.toLocaleDateString('es-CO')
    }
    return ''
  }
  
  // Try to parse as date for other types
  try {
    const parsed = new Date(value)
    if (!isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('es-CO')
    }
  } catch (e) {
    // Ignore parsing errors
  }
  
  // Return empty string to prevent [object Object] display
  return ''
}

const getStatusType = (value: string): 'success' | 'error' | 'warning' | 'pending' | 'cancelled' => {
  const lowerValue = value.toLowerCase()
  
  // Handle different status values
  if (lowerValue.includes('aprobado') || lowerValue.includes('exitoso') || lowerValue.includes('completado') || 
      lowerValue.includes('autorizada') || lowerValue.includes('autorizado') || lowerValue === 'si' || 
      lowerValue === 'sí' || lowerValue === 'yes' || lowerValue === 'true' || lowerValue === '1') {
    return 'success'
  } else if (lowerValue.includes('error') || lowerValue.includes('fallido') || lowerValue.includes('rechazado') ||
            lowerValue.includes('no autorizada') || lowerValue.includes('no autorizado') || lowerValue === 'no' ||
            lowerValue === 'false' || lowerValue === '0') {
    return 'error'
  } else if (lowerValue.includes('advertencia') || lowerValue.includes('revision') || lowerValue.includes('pendiente')) {
    return 'warning'
  } else if (lowerValue.includes('cancelado') || lowerValue.includes('anulado')) {
    return 'cancelled'
  }
  
  return 'pending'
}

const getStatusDisplayText = (value: string): string => {
  const lowerValue = value.toLowerCase()
  
  if (lowerValue === 'si' || lowerValue === 'sí' || lowerValue === 'yes' || lowerValue === 'true' || lowerValue === '1') {
    return 'Autorizada'
  } else if (lowerValue === 'no' || lowerValue === 'false' || lowerValue === '0') {
    return 'No Autorizada'
  }
  
  return value
}

export interface ReusableResultTableColumn {
  field: string
  headerName: string
  width?: number
  type?: 'string' | 'number' | 'boolean' | 'date' | 'currency' | 'status'
  sortable?: boolean
  filterable?: boolean
  editable?: boolean
  align?: 'left' | 'center' | 'right'
  valueFormatter?: (value: any) => string
  renderCell?: (params: any) => React.ReactNode
}

export interface ReusableResultTableProps {
  /** Table data */
  data: any[]
  /** Column definitions */
  columns: ReusableResultTableColumn[]
  /** Table title */
  title?: string
  /** Table subtitle */
  subtitle?: string
  /** Show selection checkboxes */
  showCheckboxes?: boolean
  /** Show toolbar */
  showToolbar?: boolean
  /** Show export options */
  showExport?: boolean
  /** Show refresh button */
  showRefresh?: boolean
  /** Show print button */
  showPrint?: boolean
  /** Custom height */
  height?: number
  /** Auto height */
  autoHeight?: boolean
  /** Loading state */
  loading?: boolean
  /** Error message */
  error?: string | null
  /** Empty message */
  emptyMessage?: string
  /** Unique table ID for state persistence */
  tableId?: string
  /** Enable state persistence */
  enableStatePersistence?: boolean
  /** Density */
  density?: 'compact' | 'standard' | 'comfortable'
  /** Custom actions */
  customActions?: React.ReactNode
  /** Selection change handler */
  onSelectionChange?: (selectedRows: any[]) => void
  /** Refresh handler */
  onRefresh?: () => void
  /** Export handler */
  onExport?: (format: 'csv' | 'excel') => void
  /** Export options */
  exportOptions?: {
    filename?: string
    includeHeader?: boolean
    includeFooter?: boolean
    includeSummary?: boolean
  }
}

export const ReusableResultTable: React.FC<ReusableResultTableProps> = ({
  data,
  columns,
  title,
  subtitle,
  showCheckboxes = false,
  showToolbar = true,
  showExport = false,
  showRefresh = false,
  showPrint = false,
  height = 400,
  autoHeight = false,
  loading = false,
  error = null,
  emptyMessage,
  tableId,
  enableStatePersistence = true,
  density = 'compact',
  customActions,
  onSelectionChange,
  onRefresh,
  onExport,
  exportOptions = {},
}) => {
  // Convert column definitions to MUI DataGrid format
  const gridColumns = useMemo((): GridColDef[] => {
    return columns.map((col) => {
      const gridCol: GridColDef = {
        field: col.field,
        headerName: col.headerName,
        width: col.width || 150,
        resizable: true, // Enable column resizing for all columns
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        editable: col.editable || false,
        align: col.align || 'left',
        headerAlign: col.align || 'left',
      }

      // Handle different column types
      switch (col.type) {
        case 'number':
          gridCol.type = 'number'
          gridCol.align = 'right'
          gridCol.headerAlign = 'right'
          break
        case 'date':
          gridCol.type = 'string' // Change to string to avoid MUI's built-in date handling
          gridCol.valueFormatter = (params: any) => formatDate(params.value)
          break
        case 'currency':
          gridCol.type = 'string' // Change to string to handle formatting better
          gridCol.align = 'right'
          gridCol.headerAlign = 'right'
          gridCol.valueFormatter = (params: any) => formatCurrency(params.value)
          break
        case 'status':
          gridCol.renderCell = (params) => {
            const value = params.value
            if (!value) return null
            
            const statusType = getStatusType(value.toString())
            const displayText = getStatusDisplayText(value.toString())
            
            return <StatusChip status={statusType} text={displayText} />
          }
          break
        case 'boolean':
          gridCol.type = 'boolean'
          gridCol.renderCell = (params) => {
            const value = params.value
            return (
              <Chip
                label={value ? 'Sí' : 'No'}
                color={value ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            )
          }
          break
        default:
          gridCol.type = 'string'
          // Add text truncation for long strings
          if (!col.renderCell) {
            gridCol.renderCell = (params) => {
              const value = params.value
              if (!value) return ''
              const stringValue = value.toString()
              if (stringValue.length > 50) {
                return (
                  <Box
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                    title={stringValue}
                  >
                    {stringValue}
                  </Box>
                )
              }
              return stringValue
            }
          }
      }

      // Apply custom formatter if provided
      if (col.valueFormatter) {
        gridCol.valueFormatter = col.valueFormatter
      }

      // Apply custom cell renderer if provided
      if (col.renderCell) {
        gridCol.renderCell = col.renderCell
      }

      return gridCol
    })
  }, [columns])

  // Add row numbers to data if not already present
  const processedData = useMemo(() => {
    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : [];
    
    return dataArray.map((row, index) => ({
      ...row,
      // Ensure each row has an ID
      id: row.id || `row_${index}`,
    }))
  }, [data])

  return (
    <Box sx={{ width: '100%' }}>
      <TreasuryDataGrid
        columns={gridColumns}
        rows={processedData}
        title={title}
        subtitle={subtitle}
        showToolbar={showToolbar}
        showExport={showExport}
        showRefresh={showRefresh}
        showPrint={showPrint}
        enableAutoHeight={autoHeight}
        minHeight={autoHeight ? 200 : height}
        maxHeight={autoHeight ? 800 : height}
        loading={loading}
        error={error}
        emptyMessage={emptyMessage}
        tableId={tableId}
        enableStatePersistence={enableStatePersistence}
        density={density}
        customActions={customActions}
        onRefresh={onRefresh}
        onExport={onExport}
        exportOptions={exportOptions}
        // Enable row selection if checkboxes are requested
        checkboxSelection={showCheckboxes}
        onRowSelectionModelChange={onSelectionChange ? (selectionModel) => {
          const selectedRows = processedData.filter(row => Array.from(selectionModel).includes(row.id))
          onSelectionChange(selectedRows)
        } : undefined}
        // Show column customization only if not a simple table
        showColumnCustomization={showToolbar}
      />
    </Box>
  )
}

export default ReusableResultTable

// Export utility functions for reuse in other components
export { parseNumericValue, formatCurrency, formatDate, getStatusType, getStatusDisplayText }